import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Trash2, Clipboard, ExternalLink, Plus, UserPlus } from "lucide-react";

/**
 * Admin → Live. Three jobs:
 *   1. Browse / upload / delete the files that live at /live/*.
 *   2. Manage the WebDAV users (username/password pairs) the live operator
 *      uses with `curl` for scripted uploads.
 *   3. Show the exact curl snippet the operator should run.
 */
export default function AdminLivePage() {
  const [files, setFiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [f, u] = await Promise.all([
        api.get("/admin/live/files"),
        api.get("/admin/live/users"),
      ]);
      setFiles(f.data || []);
      setUsers(u.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-live-page">
      <header>
        <h1 className="font-display font-bold text-2xl">Live site · /live</h1>
        <p className="text-sm text-muted-foreground">
          Upload the files that are served at <code className="text-primary">/live</code>.
          Public visitors see them without authentication; only admins and WebDAV
          users can upload.
        </p>
      </header>

      <Tabs defaultValue="files">
        <TabsList>
          <TabsTrigger value="files" data-testid="tab-live-files">Files</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-live-users">Upload users</TabsTrigger>
          <TabsTrigger value="howto" data-testid="tab-live-howto">How to upload</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-5 pt-4">
          <UploadZone onUploaded={load} />
          <FilesList files={files} loading={loading} onDeleted={load} />
        </TabsContent>

        <TabsContent value="users" className="pt-4">
          <UsersPanel users={users} onChanged={load} />
        </TabsContent>

        <TabsContent value="howto" className="pt-4">
          <HowToPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ------------------------- Upload zone ------------------------- */

function UploadZone({ onUploaded }) {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const path = window.prompt(
          `Upload "${file.name}" as:`,
          file.name,
        );
        if (!path) continue;
        const fd = new FormData();
        fd.append("file", file);
        fd.append("path", path);
        try {
          await api.post("/admin/live/upload", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`Uploaded ${path}`);
        } catch (err) {
          toast.error(`${file.name}: ${formatApiError(err)}`);
        }
      }
      await onUploaded();
    } finally {
      setBusy(false);
    }
  };

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        uploadFiles(e.dataTransfer.files);
      }}
      className={`block border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${
        drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
      }`}
      data-testid="live-upload-zone"
    >
      <input
        type="file"
        multiple
        className="hidden"
        onChange={(e) => uploadFiles(e.target.files)}
        disabled={busy}
      />
      <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
      <div className="font-medium">
        {busy ? "Uploading…" : "Drop files here, or click to pick"}
      </div>
      <div className="text-xs text-muted-foreground mt-1">
        You'll be prompted for the path (e.g. <code>index.html</code> or <code>assets/logo.png</code>). Any file type is accepted.
      </div>
    </label>
  );
}

/* ------------------------- Files list ------------------------- */

function FilesList({ files, loading, onDeleted }) {
  if (loading) {
    return <div className="text-sm text-muted-foreground">Loading…</div>;
  }
  if (!files.length) {
    return (
      <div className="border border-border bg-white p-8 text-sm text-muted-foreground text-center">
        No files uploaded yet. <code>/live</code> is showing the themed placeholder.
      </div>
    );
  }
  return (
    <div className="border border-border bg-white">
      <div className="grid grid-cols-[1fr_6rem_7rem_6rem_5rem] gap-x-4 px-4 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
        <div>Path</div>
        <div className="text-right">Size</div>
        <div>Type</div>
        <div className="text-right">Updated</div>
        <div></div>
      </div>
      {files.map((f) => (
        <FileRow key={f.path} file={f} onDeleted={onDeleted} />
      ))}
    </div>
  );
}

function FileRow({ file, onDeleted }) {
  const publicUrl = `/live/${file.path}`;
  const del = async () => {
    if (!window.confirm(`Delete /live/${file.path}?`)) return;
    try {
      await api.delete(`/admin/live/files/${encodeURI(file.path)}`);
      toast.success(`Deleted ${file.path}`);
      await onDeleted();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div
      className="grid grid-cols-[1fr_6rem_7rem_6rem_5rem] gap-x-4 px-4 py-2 text-sm border-b border-border/60 last:border-b-0 items-center"
      data-testid={`live-file-${file.path}`}
    >
      <a
        href={publicUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="truncate font-mono text-xs hover:text-primary inline-flex items-center gap-1"
      >
        {file.path}
        <ExternalLink className="h-3 w-3 opacity-60" />
      </a>
      <div className="text-right font-mono text-xs tabular-nums text-muted-foreground">
        {formatBytes(file.size)}
      </div>
      <div className="text-xs text-muted-foreground truncate">{file.content_type || "—"}</div>
      <div className="text-right text-xs text-muted-foreground">{formatDate(file.updated_at)}</div>
      <div className="text-right">
        <Button
          size="sm"
          variant="ghost"
          className="text-destructive"
          onClick={del}
          data-testid={`delete-live-${file.path}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------- Upload users ------------------------- */

function UsersPanel({ users, onChanged }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/live/users", { username, password });
      toast.success(`Created ${username}`);
      setUsername("");
      setPassword("");
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const del = async (u) => {
    if (!window.confirm(`Revoke upload access for "${u.username}"?`)) return;
    try {
      await api.delete(`/admin/live/users/${u.id}`);
      toast.success(`Revoked ${u.username}`);
      await onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_18rem] gap-6">
      <div>
        <h3 className="font-semibold mb-2">Active upload users</h3>
        {users.length === 0 ? (
          <div className="border border-border bg-white p-6 text-sm text-muted-foreground">
            No users yet. Create one to the right.
          </div>
        ) : (
          <div className="border border-border bg-white">
            {users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between px-4 py-2 border-b border-border/60 last:border-b-0"
                data-testid={`webdav-user-${u.username}`}
              >
                <div>
                  <div className="font-mono text-sm">{u.username}</div>
                  <div className="text-xs text-muted-foreground">
                    created {formatDate(u.created_at)}
                    {u.last_login ? ` · last used ${formatDate(u.last_login)}` : " · never used"}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  onClick={() => del(u)}
                  data-testid={`delete-user-${u.username}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={create} className="border border-border bg-white p-4 space-y-3 h-fit">
        <h3 className="font-semibold flex items-center gap-2"><UserPlus className="h-4 w-4" /> New upload user</h3>
        <div className="space-y-1">
          <Label htmlFor="wd-user">Username</Label>
          <Input
            id="wd-user"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="liveop"
            required
            minLength={3}
            data-testid="new-webdav-username"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="wd-pass">Password (min 8 chars)</Label>
          <Input
            id="wd-pass"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="choose something strong"
            required
            minLength={8}
            data-testid="new-webdav-password"
          />
        </div>
        <Button type="submit" disabled={busy} data-testid="create-webdav-user">
          <Plus className="h-4 w-4 mr-2" /> {busy ? "Creating…" : "Create user"}
        </Button>
        <p className="text-xs text-muted-foreground">
          The password won't be shown again — note it down and share it with the operator.
        </p>
      </form>
    </div>
  );
}

/* ------------------------- How-to ------------------------- */

function HowToPanel() {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://world-othello.com";
  const uploadBase = `${origin}/api/webdav/live`;
  const putOne = `curl -u <user>:<password> -T index.html "${uploadBase}/index.html"`;
  const putBulk = `# Push every file in the current folder, preserving sub-paths\nfind . -type f | while read f; do\n  rel="\${f#./}"\n  curl -u <user>:<password> -T "$f" "${uploadBase}/$rel"\ndone`;
  const delOne = `curl -u <user>:<password> -X DELETE "${uploadBase}/old.html"`;
  const testCreds = `curl -u <user>:<password> "${uploadBase}/_auth-check"`;

  const copy = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success("Copied");
  };

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="border-2 border-primary/50 bg-primary/5 p-4 text-sm space-y-2">
        <h3 className="font-semibold text-primary">⚠️ Two things trip people up — read first</h3>
        <ol className="list-decimal pl-5 space-y-1 text-foreground/90">
          <li>
            <strong>The upload URL is <code>{uploadBase}/&lt;path&gt;</code></strong>, NOT{" "}
            <code>{origin}/live/&lt;path&gt;</code>. The <code>/api/webdav/</code> prefix is required
            — this host's ingress only routes <code>/api/*</code> paths to the server.
          </li>
          <li>
            <strong>Credentials</strong> can be either (a) the admin login you use at <code>/login</code>
            (e.g. <code>admin@woc.org</code>) <em>or</em> (b) a dedicated upload-user created in the{" "}
            <strong>Upload users</strong> tab. Both work with <code>curl -u</code>.
          </li>
        </ol>
      </div>

      <div className="border border-border bg-white p-4 text-sm space-y-2">
        <h3 className="font-semibold">How uploading works</h3>
        <p className="text-muted-foreground">
          Anything you upload lives at <code>{origin}/live/&lt;path&gt;</code> for public viewers.
          If you upload <code>index.html</code>, visitors to <code>/live</code> see it immediately.
          Deleting a file hides it from public view; re-uploading the same path overwrites.
        </p>
        <p className="text-muted-foreground">
          <strong>Mounting /live as a network drive isn't possible on this host</strong>: the
          Cloudflare edge in front of the site blocks the extra HTTP verbs
          (<code>PROPFIND</code>, <code>LOCK</code>, …) that Finder/Explorer require.
          Drag-drop here, or scripted-upload from the command line as shown below.
        </p>
      </div>

      <Snippet title="1. Test your credentials first" text={testCreds} onCopy={copy} />
      <Snippet title="2. Upload one file" text={putOne} onCopy={copy} />
      <Snippet title="3. Push a whole folder" text={putBulk} onCopy={copy} />
      <Snippet title="4. Delete a file" text={delOne} onCopy={copy} />
    </div>
  );
}

function Snippet({ title, text, onCopy }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        <Button size="sm" variant="ghost" onClick={() => onCopy(text)}>
          <Clipboard className="h-4 w-4 mr-1" /> Copy
        </Button>
      </div>
      <pre className="border border-border bg-foreground text-background p-3 text-[11px] font-mono whitespace-pre-wrap overflow-x-auto">
        {text}
      </pre>
    </div>
  );
}

/* ------------------------- Helpers ------------------------- */

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}
